import { Box } from '@mui/material';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { LineChart } from '@mui/x-charts/LineChart';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChartSeriesToggleButtons } from './ChartSeriesToggleButtons';
import { GraphNoDataMessage } from './GraphNoDataMessage';
import { useTranslation } from 'react-i18next';
import { Graph, Series } from '../Dashboard/dashboardTypes';
import type { ChartSeriesLabelOptions } from './chartSeriesLabel';
import { chartSeriesLabel } from './chartSeriesLabel';
import {
  applySeriesColorToYAxisConfig,
  type LineGraphYAxisConfig,
  buildColoredMultiAxisChartSx,
  buildLineGraphMultiAxisLayout,
  buildRepresentativeAxisColors,
  EMPTY_LINE_GRAPH_MULTI_AXIS_LAYOUT,
  shouldUseMotorMultiAxisLayout,
  streamDataBoundsForSeriesKey,
} from './lineGraphMultiAxis';

export function buildSeriesSignature(series: Series[] | undefined): string {
  return (series ?? [])
    .map((s) => `${s.dataKey}|${s.curve ?? ''}|${s.color ?? ''}|${s.showMark ? 1 : 0}`)
    .sort()
    .join(',');
}

export function reconcileSelectedSeries(
  prevSelected: Series[],
  nextAll: Series[],
  treatEmptyAsAll: boolean = true,
  /** Stream keys present on the previous chart definition; used to auto-select newly added metrics. */
  priorChartSeriesKeys?: ReadonlySet<string>
): Series[] {
  const prevKeys = new Set(prevSelected.map((s) => s.dataKey));
  // If nothing is selected (common when charts start from placeholder graphs that have empty series),
  // default to showing all series so live graphs render.
  if (prevKeys.size === 0) return treatEmptyAsAll ? nextAll : [];

  const retained = nextAll.filter((s) => prevKeys.has(s.dataKey));
  if (!priorChartSeriesKeys?.size) return retained;

  // Auto-select metrics newly added to the chart (Choose Data) without re-enabling toggled-off series.
  const added = nextAll.filter((s) => !priorChartSeriesKeys.has(s.dataKey) && !prevKeys.has(s.dataKey));
  return added.length ? [...retained, ...added] : retained;
}

/** MUI line charts accept numbers and null only — coerce everything else to null. */
export function coerceLineChartYValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Normalizes graph rows for @mui/x-charts LineChart: valid `time` plus each series key as
 * number | null (missing or non-numeric values become null).
 */
export function sanitizeLineChartDataset(
  data: ReadonlyArray<Record<string, unknown>>,
  seriesDataKeys: readonly string[]
): Array<Record<string, number | null>> {
  return data.flatMap((row) => {
    const time = row.time;
    if (typeof time !== 'number' || !Number.isFinite(time)) return [];

    const out: Record<string, number | null> = { time };
    for (const key of seriesDataKeys) {
      out[key] = coerceLineChartYValue(row[key]);
    }
    return [out];
  });
}

export function lineChartHasPlottablePoints(dataset: ReadonlyArray<Record<string, number | null>>, seriesDataKeys: readonly string[]): boolean {
  return dataset.some((row) => seriesDataKeys.some((key) => typeof row[key] === 'number'));
}

type StreamDataBounds = { min?: number; max?: number };

export function hasCompleteStreamDataBounds(bounds: StreamDataBounds): boolean {
  return typeof bounds.min === 'number' && typeof bounds.max === 'number';
}

/** Unions per-series bounds so mixed motor types on one chart use the widest range. */
export function unionStreamDataBounds(bounds: readonly StreamDataBounds[]): { min: number; max: number } | undefined {
  let min: number | undefined;
  let max: number | undefined;

  for (const bound of bounds) {
    if (typeof bound.min === 'number') {
      min = min === undefined ? bound.min : Math.min(min, bound.min);
    }
    if (typeof bound.max === 'number') {
      max = max === undefined ? bound.max : Math.max(max, bound.max);
    }
  }

  if (typeof min === 'number' && typeof max === 'number') return { min, max };
  return undefined;
}

/** Fixed y-axis range from framework `streamData` when every resolved series has min and max. */
export function resolveFrameworkYAxisBoundsForSeriesKeys(
  seriesDataKeys: readonly string[],
  motorClassNameByPort?: ReadonlyMap<number, string>,
  mechanismClassNameByPort?: ReadonlyMap<number, string>
): { min: number; max: number } | undefined {
  if (!seriesDataKeys.length) return undefined;
  const boundsPerSeries = seriesDataKeys.map((dataKey) => streamDataBoundsForSeriesKey(dataKey, motorClassNameByPort, mechanismClassNameByPort));
  if (!boundsPerSeries.every(hasCompleteStreamDataBounds)) return undefined;
  return unionStreamDataBounds(boundsPerSeries);
}

/** Minimum tick labels on a fixed-range line chart y-axis (includes min and max). */
export const LINE_GRAPH_FIXED_Y_AXIS_MIN_TICK_COUNT = 5;

function snapInteriorAxisTickValue(value: number, min: number, max: number): number {
  const span = Math.abs(max - min);
  const decimals = span >= 100 ? 0 : span >= 10 ? 1 : 2;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Plain numeric tick text (no locale grouping) so large values like -3000 stay readable. */
export function formatLineGraphYAxisTickLabel(value: number): string {
  if (!Number.isFinite(value)) return '';
  return Number.isInteger(value) ? String(value) : String(parseFloat(value.toFixed(2)));
}

/** Longest label we may need to fit: plain formatter output or locale grouping (e.g. -5,000). */
export function lineGraphYAxisDisplayLabel(value: number): string {
  const plain = formatLineGraphYAxisTickLabel(value);
  if (!plain) return plain;
  try {
    const locale = value.toLocaleString(undefined, {
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    });
    return locale.length > plain.length ? locale : plain;
  } catch {
    return plain;
  }
}

/** MUI axis tick formatter; overrides d3 tickFormat (which adds locale grouping). */
export function lineGraphYAxisValueFormatter(value: number | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return formatLineGraphYAxisTickLabel(value);
}

/** Matches @mui/x-charts default tickSize (6) + TICK_LABEL_GAP (2) subtracted from axis width. */
export const LINE_GRAPH_Y_AXIS_TICK_LABEL_OVERHEAD_PX = 8;

const Y_AXIS_TICK_LABEL_CHAR_WIDTH_PX = 11;
const Y_AXIS_TICK_LABEL_PADDING_PX = 20;
const Y_AXIS_MIN_WIDTH_PX = 48;

/** Reserves y-axis band width so MUI shortenLabels does not ellipsize tick text. */
export function estimateLineGraphYAxisWidth(tickValues: readonly number[]): number {
  const longestLabel = tickValues.reduce((longest, value) => {
    const label = lineGraphYAxisDisplayLabel(value);
    return label.length > longest.length ? label : longest;
  }, '');
  const estimated = longestLabel.length * Y_AXIS_TICK_LABEL_CHAR_WIDTH_PX + Y_AXIS_TICK_LABEL_PADDING_PX + LINE_GRAPH_Y_AXIS_TICK_LABEL_OVERHEAD_PX;
  return Math.max(Y_AXIS_MIN_WIDTH_PX, Math.ceil(estimated));
}

/** Evenly spaced tick values from min to max, always including both endpoints. */
export function buildLinearAxisTickValues(min: number, max: number, tickCount: number = LINE_GRAPH_FIXED_Y_AXIS_MIN_TICK_COUNT): number[] {
  const count = Math.max(2, Math.floor(tickCount));
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];

  const ticks: number[] = [];
  for (let i = 0; i < count; i++) {
    if (i === 0) ticks.push(min);
    else if (i === count - 1) ticks.push(max);
    else ticks.push(snapInteriorAxisTickValue(min + ((max - min) * i) / (count - 1), min, max));
  }
  return ticks;
}

const yAxisTickLabelOverflowSx = {
  overflow: 'visible',
  textOverflow: 'clip',
} as const;

const chartSX = {
  [`& .${axisClasses.left} .${axisClasses.tickLabel}`]: yAxisTickLabelOverflowSx,
  [`& .${axisClasses.right} .${axisClasses.tickLabel}`]: yAxisTickLabelOverflowSx,
};

type NumericDatasetRow = Record<string, number | null>;

function axisDataBoundsFromDataset(
  dataset: readonly NumericDatasetRow[],
  seriesDataKeys: readonly string[]
): { min: number; max: number } | undefined {
  let min: number | undefined;
  let max: number | undefined;

  for (const row of dataset) {
    for (const key of seriesDataKeys) {
      const value = row[key];
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;
      min = min === undefined ? value : Math.min(min, value);
      max = max === undefined ? value : Math.max(max, value);
    }
  }

  if (min === undefined || max === undefined) return undefined;
  return { min, max };
}

function fallbackDomainForDegenerateAxis(value: number): { min: number; max: number } {
  // Special-case all-zero sensors so the axis always shows a sensible baseline scale.
  if (value === 0) return { min: 0, max: 1 };
  // Otherwise, pick a small symmetric window that’s stable and readable.
  const pad = Math.max(1, Math.abs(value) * 0.1);
  return { min: value - pad, max: value + pad };
}

/**
 * For unbounded multi-axis charts, MUI can end up with a "missing" axis when the plotted data
 * on that axis is degenerate (all points are the same value, e.g. temperature always 0).
 *
 * This applies a *temporary* fallback domain only in that degenerate case.
 * As soon as the data has a real span (min !== max), we remove the fallback so the axis can grow naturally.
 */
export function applyDegenerateUnboundedMultiAxisFallback(opts: {
  yAxis: readonly LineGraphYAxisConfig[];
  seriesDataKeys: readonly string[];
  dataset: readonly NumericDatasetRow[];
  yAxisIdByDataKey: ReadonlyMap<string, string>;
}): LineGraphYAxisConfig[] {
  const axisKeys = new Map<string, string[]>();
  for (const dataKey of opts.seriesDataKeys) {
    const axisId = opts.yAxisIdByDataKey.get(dataKey);
    if (!axisId) continue;
    const list = axisKeys.get(axisId);
    if (list) list.push(dataKey);
    else axisKeys.set(axisId, [dataKey]);
  }

  return opts.yAxis.map((axis) => {
    if (axis.min !== undefined && axis.max !== undefined) return axis;
    const keys = axisKeys.get(axis.id);
    if (!keys?.length) return axis;

    const bounds = axisDataBoundsFromDataset(opts.dataset, keys);
    if (!bounds) return axis;
    if (bounds.min !== bounds.max) return axis;

    const fallback = fallbackDomainForDegenerateAxis(bounds.min);
    const tickInterval = buildLinearAxisTickValues(fallback.min, fallback.max);
    return {
      ...axis,
      min: fallback.min,
      max: fallback.max,
      domainLimit: 'strict',
      width: estimateLineGraphYAxisWidth(tickInterval),
      tickInterval,
      tickNumber: tickInterval.length,
      tickLabelInterval: () => true,
      tickLabelMinGap: 0,
      valueFormatter: lineGraphYAxisValueFormatter,
    };
  });
}

interface LineGraphProps {
  graph: Graph;
  chartSeriesLabelOptions?: ChartSeriesLabelOptions;
}

const LineGraph = ({ graph, chartSeriesLabelOptions }: LineGraphProps) => {
  const [series, setSeries] = useState<Series[]>(graph.series);
  const hasUserToggledSeriesRef = useRef(false);
  const priorChartSeriesKeysRef = useRef<string[]>([]);
  // useTranslation will resolve to a string, which is the only thing accepted for x and y axis
  const { t } = useTranslation();
  const timeAxis = useMemo(() => ({ dataKey: 'time', domainLimit: 'strict', label: t('chart.xAxis.seconds', 'Time (s)') }), [t]);

  const graphValues = useMemo(() => {
    const translatedGraph = { ...graph };
    translatedGraph.xAxis = [timeAxis];
    return translatedGraph;
  }, [graph, timeAxis]);

  const seriesSignature = useMemo(() => buildSeriesSignature(graph.series), [graph.series]);

  useEffect(() => {
    // When switching to a different graph entirely, reset to the graph's full series.
    if (graph.series) setSeries(graph.series);
    hasUserToggledSeriesRef.current = false;
    priorChartSeriesKeysRef.current = graph.series?.map((s) => s.dataKey) ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph.key]);

  useEffect(() => {
    // When the series definition changes (curve/color/marks), preserve the user's selection
    // by reconciling the previously-selected keys with the new series definitions.
    if (!graph.series) return;
    const priorChartSeriesKeys = new Set(priorChartSeriesKeysRef.current);
    setSeries((prev) => reconcileSelectedSeries(prev, graph.series, !hasUserToggledSeriesRef.current, priorChartSeriesKeys));
    priorChartSeriesKeysRef.current = graph.series.map((s) => s.dataKey);
  }, [seriesSignature, graph.series]);

  const alterSeries = (dataKey: string) => {
    hasUserToggledSeriesRef.current = true;
    const index = series.findIndex((s) => s.dataKey === dataKey);
    const newSeries = [...series];

    if (index === -1) {
      const datasetToAdd = graphValues.series.find((s) => s.dataKey === dataKey);
      if (!datasetToAdd) return;
      newSeries.push(datasetToAdd);
    } else {
      newSeries.splice(index, 1);
    }
    setSeries(newSeries);
  };

  const seriesDataKeys = useMemo(() => series.map((s) => s.dataKey), [series]);
  const activeDataKeys = useMemo(() => new Set(seriesDataKeys), [seriesDataKeys]);

  const chartDataset = useMemo(() => sanitizeLineChartDataset(graphValues?.data ?? [], seriesDataKeys), [graphValues?.data, seriesDataKeys]);

  const motorClassNameByPort = chartSeriesLabelOptions?.motorClassNameByPort;
  const mechanismClassNameByPort = chartSeriesLabelOptions?.mechanismClassNameByPort;
  const useMotorMetricSeriesLabels = chartSeriesLabelOptions?.useMotorMetricSeriesLabels;

  const multiAxisLayout = useMemo(() => {
    if (!shouldUseMotorMultiAxisLayout(useMotorMetricSeriesLabels)) {
      return EMPTY_LINE_GRAPH_MULTI_AXIS_LAYOUT;
    }
    return buildLineGraphMultiAxisLayout({
      seriesDataKeys,
      motorClassNameByPort,
      mechanismClassNameByPort,
      estimateYAxisWidth: estimateLineGraphYAxisWidth,
      buildTickValues: buildLinearAxisTickValues,
      formatTickLabel: lineGraphYAxisValueFormatter,
    });
  }, [mechanismClassNameByPort, motorClassNameByPort, seriesDataKeys, useMotorMetricSeriesLabels]);

  const frameworkYAxisBounds = useMemo(
    () =>
      multiAxisLayout.useMultiAxis
        ? undefined
        : resolveFrameworkYAxisBoundsForSeriesKeys(seriesDataKeys, motorClassNameByPort, mechanismClassNameByPort),
    [mechanismClassNameByPort, multiAxisLayout.useMultiAxis, motorClassNameByPort, seriesDataKeys]
  );

  const multiAxisColors = useMemo(() => {
    if (!multiAxisLayout.useMultiAxis) return new Map<string, string>();
    return buildRepresentativeAxisColors(series, multiAxisLayout.yAxisIdByDataKey);
  }, [multiAxisLayout.useMultiAxis, multiAxisLayout.yAxisIdByDataKey, series]);

  const lineChartSx = useMemo(() => {
    if (!multiAxisLayout.useMultiAxis || multiAxisColors.size === 0) return chartSX;
    return { ...chartSX, ...buildColoredMultiAxisChartSx(multiAxisColors, axisClasses) };
  }, [multiAxisColors, multiAxisLayout.useMultiAxis]);

  const multiAxisYAxis = useMemo((): LineGraphYAxisConfig[] | undefined => {
    if (!multiAxisLayout.useMultiAxis) return undefined;
    const base = [...multiAxisLayout.yAxisById.values()].map((axis) => applySeriesColorToYAxisConfig(axis, multiAxisColors.get(axis.id)));
    return applyDegenerateUnboundedMultiAxisFallback({
      yAxis: base,
      seriesDataKeys,
      dataset: chartDataset,
      yAxisIdByDataKey: multiAxisLayout.yAxisIdByDataKey,
    });
  }, [chartDataset, multiAxisColors, multiAxisLayout, seriesDataKeys]);

  const yAxis = useMemo(() => {
    if (multiAxisYAxis) return multiAxisYAxis;
    if (frameworkYAxisBounds) {
      const tickInterval = buildLinearAxisTickValues(frameworkYAxisBounds.min, frameworkYAxisBounds.max);
      return [
        {
          id: 'default-y-axis',
          scaleType: 'linear' as const,
          min: frameworkYAxisBounds.min,
          max: frameworkYAxisBounds.max,
          domainLimit: 'strict' as const,
          width: estimateLineGraphYAxisWidth(tickInterval),
          tickInterval,
          tickNumber: tickInterval.length,
          tickLabelInterval: () => true,
          tickLabelMinGap: 0,
          valueFormatter: lineGraphYAxisValueFormatter,
        },
      ];
    }
    return graphValues.yAxis || undefined;
  }, [frameworkYAxisBounds, graphValues.yAxis, multiAxisYAxis]);

  const chartSeriesWithAxis = useMemo(
    () =>
      series.map((s) => ({
        ...s,
        label: chartSeriesLabel(t, s.dataKey, chartSeriesLabelOptions),
        ...(multiAxisLayout.useMultiAxis ? { yAxisId: multiAxisLayout.yAxisIdByDataKey.get(s.dataKey) } : {}),
      })),
    [series, t, chartSeriesLabelOptions, multiAxisLayout]
  );

  // Avoid mounting LineChart with no plottable points: @mui/x-charts warns on non-numeric
  // series values and renders its own empty-state text (duplicates GraphNoDataMessage).
  const showLineChart = series.length > 0 && lineChartHasPlottablePoints(chartDataset, seriesDataKeys);

  return graphValues ? (
    <Box display="flex" flexDirection="column" width="100%" height="100%">
      <Box flexGrow={1} minHeight={0} position="relative">
        {showLineChart ? (
          <LineChart
            hideLegend
            skipAnimation
            dataset={chartDataset}
            xAxis={graphValues.xAxis}
            yAxis={yAxis}
            series={chartSeriesWithAxis}
            sx={lineChartSx}
          />
        ) : (
          <GraphNoDataMessage />
        )}
      </Box>
      <ChartSeriesToggleButtons
        series={graphValues.series}
        activeDataKeys={activeDataKeys}
        chartSeriesLabelOptions={chartSeriesLabelOptions}
        onToggle={alterSeries}
      />
    </Box>
  ) : null;
};

LineGraph.propTypes = {
  graph: PropTypes.object.isRequired,
};
export default LineGraph;
